import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Link2, ShieldCheck, AlertTriangle } from 'lucide-react';

/**
 * Reached when the OAuth callback couldn't safely auto-link the social
 * account to an existing PIK user (email matched but the provider isn't
 * authoritative for that domain).
 *
 * User signs in with their existing PIK email/password; on success we POST
 * `/api/auth/oauth/link/complete` which finalizes the link only when the
 * authenticated user is the same as the pending-link's candidate.
 */
export default function LinkRequiredPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const provider = params.get('provider') || '';
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const providerLabel = provider === 'google' ? 'Google' : provider === 'microsoft' ? 'Microsoft' : provider;

  if (!token || !provider) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center space-y-4" data-testid="link-required-invalid">
          <AlertTriangle size={32} className="mx-auto text-amber-500" />
          <h1 className="font-heading text-xl font-bold">Invalid link request</h1>
          <p className="text-sm text-muted-foreground">This linking session has expired or was opened incorrectly.</p>
          <Button onClick={() => navigate('/login')} className="rounded-full bg-nav-blue text-white">
            Back to sign in
          </Button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Step 1: sign in with existing PIK credentials. This sets the auth cookie.
      await login(email, password);
      // Step 2: finalize the link.
      await API.post('/auth/oauth/link/complete', { token });
      toast.success(`${providerLabel} successfully linked to your account`);
      navigate('/');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not link account';
      toast.error(detail);
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-gradient-to-b from-slate-50 to-white" data-testid="link-required-page">
      <div className="w-full max-w-sm space-y-6 animate-slide-up">
        <div className="text-center space-y-2">
          <div className="w-14 h-14 mx-auto rounded-full bg-nav-blue/10 flex items-center justify-center">
            <Link2 size={24} className="text-nav-blue" />
          </div>
          <h1 className="font-heading text-2xl font-bold">Confirm linking</h1>
          <p className="text-sm text-muted-foreground">
            We found an existing PeopleiKnow account that may belong to you. To safely link your <strong>{providerLabel}</strong> sign-in, please confirm with your existing email and password.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="link-required-form">
          <div className="space-y-2">
            <Label htmlFor="link-email" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Existing email</Label>
            <Input
              id="link-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="h-12 bg-slate-50 border-transparent focus:border-nav-blue focus:bg-white rounded-lg"
              data-testid="link-required-email"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="link-password" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Password</Label>
            <Input
              id="link-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="h-12 bg-slate-50 border-transparent focus:border-nav-blue focus:bg-white rounded-lg"
              data-testid="link-required-password"
            />
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 bg-nav-blue hover:bg-nav-blue/90 text-white rounded-full font-heading font-semibold"
            data-testid="link-required-submit"
          >
            <ShieldCheck size={16} className="mr-2" /> {submitting ? 'Linking...' : `Link ${providerLabel}`}
          </Button>
        </form>

        <p className="text-[11px] text-center text-muted-foreground">
          Not your account? <button onClick={() => navigate('/login')} className="text-nav-blue underline">Go back</button>
        </p>
      </div>
    </div>
  );
}
